import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { type FocusEvent, type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
type ClickToRevealProps = {
  /** Static, dense summary shown when collapsed. */
  display: ReactNode;
  ariaLabel: string;
  /** The heavy editor, revealed on click. */
  children: () => ReactNode;
  canEdit?: boolean;
  className?: string;
  autoOpen?: boolean;
};

// Unlike InlineText, does NOT buffer a draft — the revealed editor dispatches
// live into the reducer; collapses on blur (the same `currentTarget.contains`
// guard the node save uses) or on Esc. Node-level save-on-blur owns the PATCH.
const ClickToReveal = t0 => {
  const $ = _c(28);
  const {
    display,
    ariaLabel,
    children,
    canEdit: t1,
    className,
    autoOpen: t2
  } = t0;
  const canEdit = t1 === undefined ? true : t1;
  const autoOpen = t2 === undefined ? false : t2;
  const [isOpen, setIsOpen] = useState(autoOpen);
  const wrapperRef = useRef(null);
  const swallowRestoreBlurRef = useRef(autoOpen);
  let t3;
  let t4;
  if ($[0] !== isOpen) {
    t3 = () => {
      if (!isOpen) {
        return;
      }
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      const focusInner = () => {
        const el = wrapper.querySelector("textarea, input, [contenteditable=\"true\"]");
        if (!el) {
          return false;
        }
        el.focus();
        return true;
      };
      if (focusInner()) {
        return;
      }
      wrapper.focus();
      const observer = new MutationObserver(() => {
        if (focusInner()) {
          observer.disconnect();
        }
      });
      observer.observe(wrapper, {
        childList: true,
        subtree: true
      });
      return () => observer.disconnect();
    };
    t4 = [isOpen];
    $[0] = isOpen;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsOpen(false);
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const close = t5;
  const isInsidePortalPopup = _temp;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => {
      const next = event.relatedTarget as Node | null;
      if (event.currentTarget.contains(next) || isInsidePortalPopup(next)) {
        return;
      }
      if (swallowRestoreBlurRef.current) {
        swallowRestoreBlurRef.current = false;
        return;
      }
      close();
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const handleBlur = t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event_0 => {
      if (event_0.key === "Escape") {
        event_0.stopPropagation();
        close();
      }
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const handleKeyDown = t7;
  let t8;
  if ($[6] !== canEdit) {
    t8 = () => {
      if (canEdit) {
        setIsOpen(true);
      }
    };
    $[6] = canEdit;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const open = t8;
  if (!isOpen) {
    const t9 = canEdit ? 0 : -1;
    const t10 = canEdit ? undefined : true;
    let t11;
    if ($[8] !== open) {
      t11 = event_1 => {
        if (event_1.target === event_1.currentTarget && (event_1.key === "Enter" || event_1.key === " ")) {
          event_1.preventDefault();
          open();
        }
      };
      $[8] = open;
      $[9] = t11;
    } else {
      t11 = $[9];
    }
    const t12 = canEdit && "cursor-pointer hover:bg-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent";
    let t13;
    if ($[10] !== className || $[11] !== t12) {
      t13 = cx("rounded text-left", t12, className);
      $[10] = className;
      $[11] = t12;
      $[12] = t13;
    } else {
      t13 = $[12];
    }
    let t14;
    if ($[13] !== ariaLabel || $[14] !== display || $[15] !== open || $[16] !== t10 || $[17] !== t11 || $[18] !== t13 || $[19] !== t9) {
      t14 = <div role="button" tabIndex={t9} aria-label={ariaLabel} aria-disabled={t10} onClick={open} onKeyDown={t11} className={t13}>{display}</div>;
      $[13] = ariaLabel;
      $[14] = display;
      $[15] = open;
      $[16] = t10;
      $[17] = t11;
      $[18] = t13;
      $[19] = t9;
      $[20] = t14;
    } else {
      t14 = $[20];
    }
    return t14;
  }
  let t9;
  if ($[21] !== className) {
    t9 = cx("rounded outline-none", className);
    $[21] = className;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== children) {
    t10 = children();
    $[23] = children;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <div ref={wrapperRef} tabIndex={-1} onBlur={handleBlur} onKeyDown={handleKeyDown} className={t9}>{t10}</div>;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
export default ClickToReveal;
function _temp(node) {
  if (!(node instanceof Element)) {
    return false;
  }
  return node.closest("[role=\"listbox\"], [role=\"menu\"], [data-radix-popper-content-wrapper]") !== null;
}